import { Inject, Injectable } from '@angular/core';
import { UnitService } from './unit.service';
import { Store } from '@ngxs/store';
import { AuthSelectors } from '@app/store/auth/auth.selectors';
import { buddyUtils } from '@care-connect-japan/ckweblib';
import { ApiService } from './api.service';
import * as moment from 'moment';
import { xmlToJson } from '@app/shared/common/utils';
import { APP_BASE_HREF } from '@angular/common';
import { NoteBlobReq } from '@app/models/noteblob.model';

interface NoteData {
  corpcd: string;
  officecd: string;
  blockcd: string;
  subcd: string;
  unitcd: string;
  ttlkbn: string;
  ttlcd: string;
  contents?: string;
  v2contents?: string;
  origin?: string;
  noteid?: number;
  noteblobs?: PictureDTO[]
}

interface PictureDTO {
  delflg: boolean;
  objkbn: string;
  id?: string | number;
  bldata?: string;
  ext: string;
  upddate: string;
  insdate: string;
  file?: File;
  link?: string;
}


interface LibOperation {
  duplicate?: boolean;
  view?: boolean;
  inactive?: boolean;
  create?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LibService {
  enQueue: any;
  constructor(private unitService: UnitService, private apiService: ApiService, private store: Store, @Inject(APP_BASE_HREF) private baseHref: string,) {
    this.enQueue = this.queue()
  }

  /**
   * use for enqueue and dequeue async request
   */
  private queue() {
    const items: any[] = [];
    const dequeue = () => {
      // no work to do
      if (!items[0]) return
      items[0]()
        // .then(onResolve)
        // .catch(onReject)
        .then(() => items.shift())
        .then(dequeue);
    }
    const enqueue = (func: Function) => {
      items.push(func)
      if (items.length === 1) dequeue()
    }
    return enqueue;
  }


  async getNoteParam(noteData: NoteData) {
    let param = {
      ttlkbn: noteData.ttlkbn,
      ttlcd: noteData.ttlcd,
      sdate: moment().toDate().toUTCString()
    }
    let title = this.enQueue(async () => await this.apiService.fetchTitleData(param));
    return { noteData, device: 1, titleObject: title };
  }

  public async noteblobFetch(title: any, noteData: any) {
    let fieldids = this.noteBlobDetected(title);
    const { objkbn, objcd, noteid } = noteData;
    if (fieldids.length > 0) {
      const payload: NoteBlobReq = {
        orgId: this.baseHref.slice(1),
        objkbn,
        objcd,
        noteid: noteid?.toString(),
        // fieldids
      }
      let _noteBlobs = await this.apiService.setNoteBlobs(payload) || [];
      return _noteBlobs?.map(x => { return { id: x.fieldid, link: x.preSignURL } });
    }
    return [];
  }

  /**
   * 
   * @param location occurred location
   * @param contents log contents
   * @returns 
   */
  errorLog(location: string, contents: string = '') {
    return this.apiService.postErrorLog({ location, contents }).subscribe(
      (res) => {
        if (res) {
          console.log('UNEXPECTED LOGGED')
        }
      }
    );
  }

  private readonly WGT_IMG: string = '8';

  private noteBlobDetected(title: any): string[] {
    if (!title || !title.ttlstruct) return [];
    const formatTtlStruct = xmlToJson(title.ttlstruct);
    const items = formatTtlStruct[0]?.children.filter((x: any) => x.tagName === "itm")?.flat();
    const noteblobFields = items.filter((x: any) => x.wtype == this.WGT_IMG).map((x: any) => x.id);
    return noteblobFields;
  }

  changeTitleModified(content: string): string {
    if (!content || content == '') return '';
    const cleaned = content.replace(/<itm\b[^>]*>[\s\S]*?<\/itm>/g, "").trim();
    return cleaned;
  }

  async getNoteTitleParams(noteData: NoteData, operation: LibOperation) {
    const { ttlkbn, ttlcd, contents, v2contents, noteid } = { ...noteData };
    let param = {
      ttlkbn: noteData.ttlkbn,
      ttlcd: noteData.ttlcd,
      sdate: moment().toDate().toUTCString()
    }
    let title = await this.apiService.fetchTitleData(param);
    if (v2contents || contents || (noteid && noteid >= 0)) {
      return {
        noteData: { ...noteData },
        operation: this.getOperation(operation),
        device: 1, // 1: mobile phone
        titleObject: title
      };
    }

    const objcd = this.unitService.getObjcd(noteData);
    const params = {
      ttlkbn,
      ttlcd,
      objcd,
      device: 1, // 1: mobile phone
      titleObject: title
    };

    const { inactive } = operation;
    if (inactive) {
      return { ...params, operation: this.getOperation({ inactive }) };
    }
    return params;
  }

  async getFaceSheetParams(noteData: NoteData, operation: LibOperation) {
    const { ttlkbn, ttlcd } = noteData;
    const objectcd = this.store.selectSnapshot(AuthSelectors.objectcd);
    let obj = buddyUtils.default.extractOffice(objectcd);
    let param = {
      ttlkbn: noteData.ttlkbn,
      ttlcd: noteData.ttlcd,
      sdate: moment().toDate().toUTCString()
    }
    let title = await this.apiService.fetchTitleData(param);
    if (noteData) {
      return {
        noteData: { ...noteData, ...obj },
        operation: this.getOperation(operation),
        device: 1, // 1: mobile phone
        titleObject: title
      };
    }

    const objcd = this.unitService.getObjcd(noteData);
    const params = {
      ttlkbn,
      ttlcd,
      objcd,
      device: 1, // 1: mobile phone
      titleObject: title
    };

    const { inactive } = operation;
    if (inactive) {
      return { ...params, operation: this.getOperation({ inactive }) };
    }
    return params;
  }

  private getOperation({ duplicate, view, inactive, create }: LibOperation) {
    if (create) return 0;
    if (duplicate) return 2;
    if (view) return 3;
    if (inactive) return 4;
    return 1; // edit
  }
}
